import type { MouseEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Footer() {
  const location = useLocation();

  const handleTopNavigation = (event: MouseEvent<HTMLAnchorElement>, path: string) => {
    const [pathname, hash = ''] = path.split('#');
    const targetPath = pathname || location.pathname;

    if (targetPath !== location.pathname) {
      return;
    }

    event.preventDefault();

    if (!hash) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      return;
    }

    const sectionId = ['system', 'app', 'web'].includes(hash) ? 'services-tabs' : hash;
    const section = document.getElementById(sectionId);

    if (!section) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      return;
    }

    window.location.hash = hash;
    section.scrollIntoView({ behavior: 'auto', block: 'start' });
    window.scrollBy(0, -96);
  };

  return (
    <footer className="bg-teal-900 text-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-br from-teal-400 to-cyan-400">
                <i className="ri-water-flash-line text-white text-sm"></i>
              </div>
              <span className="font-bold text-xl" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                Ocean
              </span>
            </div>
            <p className="text-teal-200 text-sm leading-relaxed mb-4 max-w-xs">
              各種システム・ソフトウェアの企画・開発・設計から、アプリ開発、Webサービス運営まで。
              テクノロジーで、あなたのビジネスを次のステージへ。
            </p>
            <div className="flex items-center gap-2 text-teal-300 text-sm">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-mail-line text-xs"></i>
              </div>
              <a href="mailto:banking@oceansubstitute.com" className="hover:text-white transition-colors cursor-pointer">
                banking@oceansubstitute.com
              </a>
            </div>
            <div className="flex items-center gap-2 text-teal-300 text-sm mt-2">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-line text-xs"></i>
              </div>
              <a href="tel:08044800273" className="hover:text-white transition-colors cursor-pointer">
                080-4480-0273
              </a>
            </div>
          </div>

          {/* Links */}
          <div>
            <h4 className="font-semibold text-sm mb-4 text-teal-100 uppercase tracking-wider">サービス</h4>
            <ul className="space-y-2">
              {[
                { label: 'システム開発', path: '/services#system' },
                { label: 'アプリ開発', path: '/services#app' },
                { label: 'Webサービス運営', path: '/services#web' },
                { label: '料金プラン', path: '/plans' },
              ].map((item) => (
                <li key={item.label}>
                  <Link
                    to={item.path}
                    onClick={(event) => handleTopNavigation(event, item.path)}
                    className="text-teal-300 text-sm hover:text-white transition-colors cursor-pointer"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-semibold text-sm mb-4 text-teal-100 uppercase tracking-wider">会社情報</h4>
            <ul className="space-y-2">
              {[
                { label: '会社概要', path: '/about' },
                { label: 'ニュース', path: '/news' },
                { label: 'お問い合わせ', path: '/contact' },
                { label: 'プライバシーポリシー', path: '/privacy' },
              ].map((item) => (
                <li key={item.label}>
                  <Link
                    to={item.path}
                    onClick={(event) => handleTopNavigation(event, item.path)}
                    className="text-teal-300 text-sm hover:text-white transition-colors cursor-pointer"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-4 text-teal-400 text-xs leading-relaxed">
              <p>〒530-0043 大阪府大阪市北区天満１丁目４−１９ キオプラザ天満橋</p>
            </div>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-teal-800 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-teal-400 text-xs">
            &copy; {new Date().getFullYear()} 株式会社Ocean. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <Link to="/privacy" onClick={(event) => handleTopNavigation(event, '/privacy')} className="text-teal-400 text-xs hover:text-white transition-colors cursor-pointer">
              プライバシーポリシー
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
